import { db } from './db';

// Helper: get existing group or create — uses compound index [companyId+name]
async function getOrCreateGroup(companyId: number, name: string, data: { parentGroupId: number | null; nature: "Assets" | "Liabilities" | "Income" | "Expenses" }): Promise<number> {
  const existing = await db.groups.where('[companyId+name]').equals([companyId, name]).first();
  if (existing) return existing.id!;
  return db.groups.add({ companyId, name, parentGroupId: data.parentGroupId, nature: data.nature, isDefault: true } as any);
}

// Helper: get existing ledger or create — uses compound index [companyId+name]
async function getOrCreateLedger(companyId: number, name: string, data: Record<string, any>): Promise<number> {
  const existing = await db.ledgers.where('[companyId+name]').equals([companyId, name]).first();
  if (existing) return existing.id!;
  return db.ledgers.add({ companyId, name, ...data } as any);
}

export async function seedCompany(companyId: number) {
  // Primary Groups (idempotent)
  const capitalId       = await getOrCreateGroup(companyId, 'Capital Account',     { parentGroupId: null, nature: 'Liabilities' });
  const currentAssetsId = await getOrCreateGroup(companyId, 'Current Assets',      { parentGroupId: null, nature: 'Assets' });
  const currentLiabId   = await getOrCreateGroup(companyId, 'Current Liabilities', { parentGroupId: null, nature: 'Liabilities' });
  const fixedAssetsId   = await getOrCreateGroup(companyId, 'Fixed Assets',        { parentGroupId: null, nature: 'Assets' });
  const salesId         = await getOrCreateGroup(companyId, 'Sales Accounts',      { parentGroupId: null, nature: 'Income' });
  const purchaseId      = await getOrCreateGroup(companyId, 'Purchase Accounts',   { parentGroupId: null, nature: 'Expenses' });
  const directExpId     = await getOrCreateGroup(companyId, 'Direct Expenses',     { parentGroupId: null, nature: 'Expenses' });
  const indirectExpId   = await getOrCreateGroup(companyId, 'Indirect Expenses',   { parentGroupId: null, nature: 'Expenses' });
  const dutiesTaxesId   = await getOrCreateGroup(companyId, 'Duties & Taxes',      { parentGroupId: currentLiabId, nature: 'Liabilities' });

  // Sub Groups (idempotent)
  const bankId      = await getOrCreateGroup(companyId, 'Bank Accounts',    { parentGroupId: currentAssetsId, nature: 'Assets' });
  const cashId      = await getOrCreateGroup(companyId, 'Cash-in-Hand',     { parentGroupId: currentAssetsId, nature: 'Assets' });
  const debtorsId   = await getOrCreateGroup(companyId, 'Sundry Debtors',   { parentGroupId: currentAssetsId, nature: 'Assets' });
  const creditorsId = await getOrCreateGroup(companyId, 'Sundry Creditors', { parentGroupId: currentLiabId,   nature: 'Liabilities' });
  
  // Suppress unused var warnings
  void capitalId; void fixedAssetsId; void directExpId; void indirectExpId;

  // Default Ledgers (idempotent)
  await getOrCreateLedger(companyId, 'Cash',              { groupId: cashId,        openingBalance: 0, balanceType: 'Dr', isDefault: true });
  await getOrCreateLedger(companyId, 'Bank A/c',          { groupId: bankId,        openingBalance: 0, balanceType: 'Dr', isDefault: true });
  await getOrCreateLedger(companyId, 'Profit & Loss A/c', { groupId: null,          openingBalance: 0, balanceType: 'Cr', isDefault: true });
  await getOrCreateLedger(companyId, 'Sales A/c',         { groupId: salesId,       openingBalance: 0, balanceType: 'Cr', isDefault: true });
  await getOrCreateLedger(companyId, 'Purchase A/c',      { groupId: purchaseId,    openingBalance: 0, balanceType: 'Dr', isDefault: true });

  // GST Ledgers (idempotent)
  await getOrCreateLedger(companyId, 'CGST', { groupId: dutiesTaxesId, openingBalance: 0, balanceType: 'Cr', isDefault: true });
  await getOrCreateLedger(companyId, 'SGST', { groupId: dutiesTaxesId, openingBalance: 0, balanceType: 'Cr', isDefault: true });
  await getOrCreateLedger(companyId, 'IGST', { groupId: dutiesTaxesId, openingBalance: 0, balanceType: 'Cr', isDefault: true });
  await getOrCreateLedger(companyId, 'CESS', { groupId: dutiesTaxesId, openingBalance: 0, balanceType: 'Cr', isDefault: true });

  // Stock Groups (idempotent)
  for (const sgName of ['Electronics', 'Furniture', 'Stationery', 'Raw Materials']) {
    const exists = await db.stockGroups.where('[companyId+name]').equals([companyId, sgName]).first();
    if (!exists) await db.stockGroups.add({ companyId, name: sgName, parentGroupId: null });
  }

  // Stock Categories (idempotent)
  const existingStockCat = await db.stockCategories.where('[companyId+name]').equals([companyId, 'Primary']).first();
  if (!existingStockCat) await db.stockCategories.add({ companyId, name: 'Primary', parentCategoryId: null });

  // Units (idempotent) — uses compound index [companyId+symbol]
  const unitDefs = [
    { symbol: 'Nos', formalName: 'Numbers',   decimalPlaces: 0 },
    { symbol: 'Pcs', formalName: 'Pieces',    decimalPlaces: 0 },
    { symbol: 'Kgs', formalName: 'Kilograms', decimalPlaces: 3 },
    { symbol: 'Ltr', formalName: 'Liters',    decimalPlaces: 3 },
    { symbol: 'Mtr', formalName: 'Meters',    decimalPlaces: 2 },
  ];
  for (const u of unitDefs) {
    const exists = await db.units.where('[companyId+symbol]').equals([companyId, u.symbol]).first();
    if (!exists) await db.units.add({ companyId, ...u });
  }

  // Default Location (idempotent)
  const existingLoc = await db.locations.where('[companyId+name]').equals([companyId, 'Main Location']).first();
  if (!existingLoc) await db.locations.add({ companyId, name: 'Main Location', parentLocationId: null });

  // Currencies (idempotent) — uses compound index [companyId+code]
  const now = new Date().toISOString();
  const existingINR = await db.currencies.where('[companyId+code]').equals([companyId, 'INR']).first();
  if (!existingINR) await db.currencies.add({ companyId, name: 'Indian Rupee', symbol: '₹', code: 'INR', isBaseCurrency: true, exchangeRate: 1, createdAt: now });
  const existingUSD = await db.currencies.where('[companyId+code]').equals([companyId, 'USD']).first();
  if (!existingUSD) await db.currencies.add({ companyId, name: 'US Dollar', symbol: '$', code: 'USD', isBaseCurrency: false, exchangeRate: 83.5, createdAt: now });

  // Salary Components (idempotent)
  const salaryDefs = [
    { name: 'HRA',        type: 'Allowance' as const, valueType: 'Percentage' as const, percentageValue: 40 },
    { name: 'Conveyance', type: 'Allowance' as const, valueType: 'Fixed' as const },
    { name: 'PF',         type: 'Deduction' as const, valueType: 'Percentage' as const, percentageValue: 12 },
    { name: 'ESI',        type: 'Deduction' as const, valueType: 'Percentage' as const, percentageValue: 0.75 },
    { name: 'TDS',        type: 'Deduction' as const, valueType: 'Fixed' as const },
  ];
  for (const sc of salaryDefs) {
    const exists = await db.salaryComponents.where('[companyId+name]').equals([companyId, sc.name]).first();
    if (!exists) await db.salaryComponents.add({ companyId, ...sc });
  }

  // Voucher Types (idempotent)
  const vtDefs = [
    { name: 'Contra',      affectsStock: false, stockDirection: 'None' as const },
    { name: 'Receipt',     affectsStock: false, stockDirection: 'None' as const },
    { name: 'Payment',     affectsStock: false, stockDirection: 'None' as const },
    { name: 'Journal',     affectsStock: false, stockDirection: 'None' as const },
    { name: 'Sales',       affectsStock: true,  stockDirection: 'Out' as const },
    { name: 'Purchase',    affectsStock: true,  stockDirection: 'In' as const },
    { name: 'Credit Note', affectsStock: true,  stockDirection: 'In' as const },
    { name: 'Debit Note',  affectsStock: true,  stockDirection: 'Out' as const },
  ];
  for (const vt of vtDefs) {
    const exists = await db.voucherTypes.where('[companyId+name]').equals([companyId, vt.name]).first();
    if (!exists) await db.voucherTypes.add({ companyId, ...vt, numberingMethod: 'auto', startingNumber: 1, currentNumber: 1, isSystem: true, createdAt: now });
  }
}

export async function seedDatabase() {
  // No global auto-seed — users create their own company
}

export async function seedDemoData(companyId: number) {
  const debtorsGroup   = await db.groups.where('[companyId+name]').equals([companyId, 'Sundry Debtors']).first();
  const creditorsGroup = await db.groups.where('[companyId+name]').equals([companyId, 'Sundry Creditors']).first();
  const currentAssets  = await db.groups.where('[companyId+name]').equals([companyId, 'Current Assets']).first();
  const currentLiab    = await db.groups.where('[companyId+name]').equals([companyId, 'Current Liabilities']).first();

  const debtorsId  = debtorsGroup  ? debtorsGroup.id!  : (currentAssets ? currentAssets.id! : null);
  const creditorsId = creditorsGroup ? creditorsGroup.id! : (currentLiab ? currentLiab.id! : null);

  await db.ledgers.add({ companyId, name: 'Demo Customer', groupId: debtorsId,   openingBalance: 5000, balanceType: 'Dr', isDefault: false, address: 'Test City', state: 'Delhi', gstRegistrationType: 'Regular', gstin: '07AAAAA0000A1Z5' });
  await db.ledgers.add({ companyId, name: 'Demo Supplier', groupId: creditorsId, openingBalance: 2000, balanceType: 'Cr', isDefault: false, address: 'Test City', state: 'Delhi', gstRegistrationType: 'Regular', gstin: '07BBBBB0000B1Z5' });

  const stockGroup = await db.stockGroups.where('[companyId+name]').equals([companyId, 'Electronics']).first();
  const unit       = await db.units.where('[companyId+symbol]').equals([companyId, 'Nos']).first();
  const category   = await db.stockCategories.where('[companyId+name]').equals([companyId, 'Primary']).first();

  if (unit && stockGroup && category) {
    await db.stockItems.add({ companyId, name: 'Demo Laptop', stockGroupId: stockGroup.id!, stockCategoryId: category.id!, unitId: unit.id!, openingBalance: 10, openingRate: 50000, openingValue: 500000, gstRate: 18, reorderLevel: 5, minStockLevel: 2 });
  }

  await db.employees.add({ companyId, employeeId: 'EMP001', name: 'John Doe', department: 'IT', designation: 'Developer', joiningDate: new Date().toISOString().split('T')[0], status: 'Active', basicSalary: 50000 });
}
